<script setup lang="ts">
  import GlobalAiChat from '@/components/app/chat/global/GlobalAiChat.vue'
  import GlobalAiChatIntro from '@/components/app/chat/global/GlobalAiChatIntro.vue'
  import { useMetrics } from '@/composables/useMetrics'
  import { useTranslation } from '@/composables/useTranslation'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useGlobalChatStore } from '@/store/app/globalChat/globalChatStore'
  import { AIChatSessionTypeEnum, EventNameEnum } from '@/types/generated'
  import { Loader2 } from 'lucide-vue-next'
  import { computed, onMounted, provide, ref } from 'vue'
  import { useRoute } from 'vue-router'

  const route = useRoute()
  const globalChatStore = useGlobalChatStore()
  const userStore = useUserStore()
  const { trackEvent } = useMetrics()
  const { t } = useTranslation({
    en: {
      retry: 'Try again'
    },
    de: {
      retry: 'Erneut versuchen'
    }
  })

  const activeFolderId = ref<string | null>(null)
  const showIntro = ref(false)
  const isLoading = ref(true)

  const activeSessionGroup = computed(() => {
    if (!activeFolderId.value) return null
    return (
      globalChatStore.sessionGroups.find((group) => group.uuid === activeFolderId.value) || null
    )
  })

  provide('sessionType', AIChatSessionTypeEnum.GLOBAL)
  provide('sessionGroupData', activeSessionGroup)

  const handleIntroAccepted = () => {
    showIntro.value = false
  }

  const loadInitialGlobalChatData = async () => {
    await globalChatStore.loadSessionGroups()
    await globalChatStore.loadGlobalPromptTemplates()
    await globalChatStore.loadStandaloneGlobalChatSessions()
    const groupUuid = route.params.groupUuid as string
    if (groupUuid) {
      activeFolderId.value = groupUuid
      await globalChatStore.loadChatSessions(groupUuid)
    }
  }

  const retryInitialLoad = async () => {
    globalChatStore.clearError()
    isLoading.value = true
    try {
      await loadInitialGlobalChatData()
    } finally {
      isLoading.value = false
    }
  }

  onMounted(async () => {
    globalChatStore.clearError()
    try {
      if (!userStore.user) {
        await userStore.fetchUser()
      }

      showIntro.value = !(userStore.user as any)?.global_ai_chat_intro_seen

      if (userStore.user && !(userStore.user as any)?.global_ai_chat_badge_dismissed) {
        try {
          const { UsersService } = await import('@/types/generated')
          await UsersService.usersPartialUpdate({
            uuid: userStore.user.uuid!,
            requestBody: {
              global_ai_chat_badge_dismissed: true
            } as any
          })
          if (userStore.user) {
            ;(userStore.user as any).global_ai_chat_badge_dismissed = true
          }
        } catch (error) {
          console.error('Failed to dismiss badge:', error)
        }
      }

      await trackEvent(EventNameEnum.GLOBAL_AI_CHAT_VISITED)

      await loadInitialGlobalChatData()
    } finally {
      isLoading.value = false
    }
  })
</script>

<template>
  <div class="flex h-[calc(100vh-60px)] w-full">
    <div
      v-if="isLoading"
      class="flex h-full w-full items-center justify-center bg-gray-50"
    >
      <Loader2 class="h-8 w-8 animate-spin text-gray-400" />
    </div>
    <div
      v-else-if="globalChatStore.error"
      class="flex h-full w-full flex-col items-center justify-center gap-4 bg-gray-50 px-6"
      role="alert"
      data-cy="ai-assistant-chat-load-error"
    >
      <p class="max-w-md text-center text-sm text-gray-700">
        {{ globalChatStore.error }}
      </p>
      <button
        type="button"
        class="rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700"
        data-cy="ai-assistant-chat-load-retry"
        @click="retryInitialLoad"
      >
        {{ t('retry') }}
      </button>
    </div>
    <GlobalAiChatIntro
      v-else-if="showIntro"
      @intro-accepted="handleIntroAccepted"
    />
    <GlobalAiChat
      v-else
      :session-group-uuid="activeFolderId || undefined"
      :session-group="activeSessionGroup || undefined"
    />
  </div>
</template>
